/**
 * Centralized logging utility for the frontend
 * Provides consistent logging across the application with different log levels
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

export enum LogCategory {
  WALLET = 'WALLET',
  CONTRACT = 'CONTRACT',
  NFC = 'NFC',
  API = 'API',
  UI = 'UI',
  ERROR = 'ERROR',
  GENERAL = 'GENERAL',
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: any;
  error?: Error;
}

class Logger {
  private currentLevel: LogLevel;
  private logs: LogEntry[] = [];
  private maxLogs: number = 1000; // Keep last 1000 logs in memory

  constructor() {
    // Set log level based on environment
    this.currentLevel = process.env.REACT_APP_NODE_ENV === 'development' ? LogLevel.DEBUG : LogLevel.INFO;
  }

  private formatTimestamp(): string {
    return new Date().toISOString();
  }

  private formatMessage(level: LogLevel, category: LogCategory, message: string, data?: any, error?: Error): string {
    const timestamp = this.formatTimestamp();
    const levelName = LogLevel[level];
    // const prefix = `[${timestamp}] [${levelName}] [${category}]`;
    
    // let formattedMessage = `${prefix} ${message}`;
    let formattedMessage = `${message}`;
    
    if (data) {
      formattedMessage += ` | Data: ${JSON.stringify(data, null, 2)}`;
    }
    
    if (error) {
      formattedMessage += ` | Error: ${error.message}`;
      if (error.stack) {
        formattedMessage += ` | Stack: ${error.stack}`;
      }
    }
    
    return formattedMessage;
  }

  private log(level: LogLevel, category: LogCategory, message: string, data?: any, error?: Error): void {
    if (level < this.currentLevel) {
      return; // Skip logs below current level
    }

    const logEntry: LogEntry = {
      timestamp: this.formatTimestamp(),
      level,
      category,
      message,
      data,
      error,
    };

    // Add to memory logs
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift(); // Remove oldest log
    }

    // Console output with colors
    const formattedMessage = this.formatMessage(level, category, message, data, error);
    
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(`%c${formattedMessage}`, 'color: #888');
        break;
      case LogLevel.INFO:
        console.info(`%c${formattedMessage}`, 'color: #2196F3');
        break;
      case LogLevel.WARN:
        console.warn(`%c${formattedMessage}`, 'color: #FF9800');
        break;
      case LogLevel.ERROR:
        console.error(`%c${formattedMessage}`, 'color: #F44336');
        break;
    }
  }

  // Public logging methods
  debug(category: LogCategory, message: string, data?: any): void {
    this.log(LogLevel.DEBUG, category, message, data);
  }

  info(category: LogCategory, message: string, data?: any): void {
    this.log(LogLevel.INFO, category, message, data);
  }

  warn(category: LogCategory, message: string, data?: any): void {
    this.log(LogLevel.WARN, category, message, data);
  }

  error(category: LogCategory, message: string, error?: Error, data?: any): void {
    this.log(LogLevel.ERROR, category, message, data, error);
  }

  // Specialized logging methods
  wallet(message: string, data?: any): void {
    this.info(LogCategory.WALLET, message, data);
  }

  walletError(message: string, error: Error, data?: any): void {
    this.error(LogCategory.WALLET, message, error, data);
  }

  contract(message: string, data?: any): void {
    this.info(LogCategory.CONTRACT, message, data);
  }

  contractError(message: string, error: Error, data?: any): void {
    this.error(LogCategory.CONTRACT, message, error, data);
  }

  nfc(message: string, data?: any): void {
    this.info(LogCategory.NFC, message, data);
  }

  nfcError(message: string, error: Error, data?: any): void {
    this.error(LogCategory.NFC, message, error, data);
  }

  api(message: string, data?: any): void {
    this.info(LogCategory.API, message, data);
  }

  apiError(message: string, error: Error, data?: any): void {
    this.error(LogCategory.API, message, error, data);
  }

  ui(message: string, data?: any): void {
    this.debug(LogCategory.UI, message, data);
  }

  // Utility methods
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs(): void {
    this.logs = [];
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  setLogLevel(level: LogLevel): void {
    this.currentLevel = level;
    this.info(LogCategory.GENERAL, `Log level changed to ${LogLevel[level]}`);
  }
}

// Create singleton instance
export const logger = new Logger();

// Export for global access in development
if (process.env.REACT_APP_NODE_ENV === 'development') {
  (window as any).logger = logger;
}
